import {
  useCallback,
  useEffect,
  useRef,
  useState,
} from 'react';

import {
  CameraCaptureError,
  cameraConstraints,
  createPhotoFileFromVideo,
  getCameraCaptureError,
  stopMediaStream,
} from '../../helpers/cameraCapture';

interface IUseCameraCapture {
  onCapture: (file: File) => void;
}

const useCameraCapture = ({ onCapture }: IUseCameraCapture) => {
  const videoRef = useRef<HTMLVideoElement | null>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [cameraError, setCameraError] = useState<CameraCaptureError | null>(
    null,
  );
  const [isCameraReady, setIsCameraReady] = useState(false);
  const [isCapturing, setIsCapturing] = useState(false);

  const stopCamera = useCallback(() => {
    stopMediaStream(streamRef.current);
    streamRef.current = null;

    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  useEffect(() => {
    let isActive = true;

    const handleStream = (stream: MediaStream) => {
      if (!isActive) {
        stopMediaStream(stream);
        return;
      }

      streamRef.current = stream;

      if (videoRef.current) {
        videoRef.current.srcObject = stream;
      }
    };

    const handleStreamError = (error: unknown) => {
      if (isActive) {
        setCameraError(getCameraCaptureError(error));
      }
    };

    navigator.mediaDevices
      .getUserMedia(cameraConstraints)
      .then(handleStream)
      .catch(handleStreamError);

    return () => {
      isActive = false;
      stopCamera();
    };
  }, [stopCamera]);

  const handleVideoReady = useCallback(() => {
    setIsCameraReady(true);
  }, []);

  const handleCapture = useCallback(() => {
    const video = videoRef.current;

    if (!video || !isCameraReady) {
      return;
    }

    setIsCapturing(true);
    createPhotoFileFromVideo(video)
      .then((file) => {
        stopCamera();
        onCapture(file);
      })
      .catch(() => {
        setCameraError(CameraCaptureError.captureFailed);
      })
      .finally(() => {
        setIsCapturing(false);
      });
  }, [isCameraReady, onCapture, stopCamera]);

  const canCapture = isCameraReady && !isCapturing && !cameraError;

  return {
    cameraError,
    canCapture,
    handleCapture,
    handleVideoReady,
    videoRef,
  };
};

export default useCameraCapture;
